/**
 * Трёхстороннее слияние значений записи (docs/research/05-collaboration.md,
 * п. 2): с чего человек начинал (base), что сделал он (mine), что сделал
 * другой (theirs).
 *
 * - Поле поменял только другой — берётся его значение.
 * - Только я — моё.
 * - Оба, и по-разному — конфликт: выбирает человек, молча никто не теряет.
 *
 * Переводы (_i18n) сводятся по языку и полю, а не целиком: перевод на
 * английский и правка заголовка на русском — не конфликт.
 */
export interface MergeResult {
  merged: Record<string, unknown>;
  /** Поля, которые поменяли оба и по-разному: ключи вида `title` или `_i18n.en.title`. */
  conflicts: string[];
  /** Поля, которые поменял другой и которые вошли в результат. */
  theirs: string[];
}

const I18N = '_i18n';

export function threeWay(
  base: Record<string, unknown>,
  mine: Record<string, unknown>,
  theirs: Record<string, unknown>,
): MergeResult {
  const b = flatten(base);
  const m = flatten(mine);
  const t = flatten(theirs);

  const merged: Record<string, unknown> = {};
  const conflicts: string[] = [];
  const taken: string[] = [];

  for (const key of new Set([...Object.keys(b), ...Object.keys(m), ...Object.keys(t)])) {
    const iChanged = !same(b[key], m[key]);
    const theyChanged = !same(b[key], t[key]);

    if (iChanged && theyChanged && !same(m[key], t[key])) {
      conflicts.push(key);
      // До выбора человека в результате — моё: форма не должна прыгать.
      merged[key] = m[key];
    } else if (iChanged) {
      merged[key] = m[key];
    } else {
      merged[key] = t[key];

      if (theyChanged) {
        taken.push(key);
      }
    }
  }

  return { merged: unflatten(merged), conflicts, theirs: taken };
}

/** Применить выбор по конфликтам: true — взять их значение. */
export function resolve(
  merged: Record<string, unknown>,
  theirs: Record<string, unknown>,
  takeTheirs: Record<string, boolean>,
): Record<string, unknown> {
  const flat = flatten(merged);
  const other = flatten(theirs);

  for (const [key, take] of Object.entries(takeTheirs)) {
    if (take) {
      flat[key] = other[key];
    }
  }

  return unflatten(flat);
}

/** Значение по плоскому ключу. */
export function valueAt(values: Record<string, unknown>, key: string): unknown {
  return flatten(values)[key];
}

function flatten(values: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(values)) {
    if (key === I18N && isObject(value)) {
      for (const [lang, fields] of Object.entries(value)) {
        if (!isObject(fields)) {
          continue;
        }

        for (const [field, text] of Object.entries(fields)) {
          out[`${I18N}.${lang}.${field}`] = text;
        }
      }

      continue;
    }

    out[key] = value;
  }

  return out;
}

function unflatten(flat: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(flat)) {
    if (!key.startsWith(`${I18N}.`)) {
      out[key] = value;

      continue;
    }

    const [, lang = '', field = ''] = key.split('.');
    const all = (isObject(out[I18N]) ? out[I18N] : {}) as Record<string, Record<string, unknown>>;

    all[lang] = { ...(all[lang] ?? {}), [field]: value };
    out[I18N] = all;
  }

  return out;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function same(a: unknown, b: unknown): boolean {
  const empty = (v: unknown) => v === null || v === undefined || v === '';

  if (empty(a) && empty(b)) {
    return true;
  }

  return JSON.stringify(a) === JSON.stringify(b);
}
